/** Default entries shown on the dashboard card (admin: 6; company passes `limit={10}`). */
export const RECENT_ACTIVITY_LIMIT = 6;

/**
 * Activity log module → its `UIChip` light colour. Shared by the dashboard
 * card (admin + company portals) and the full activity log. Labels live in
 * `dashboard.recentActivity.modules`.
 */
export const ACTIVITY_MODULE_COLOR = {
  companies: "primarySubtle",
  companyAccount: "primarySubtle",
  teamPermissions: "secondary",
  clients: "secondary",
  events: "info",
  joiningRequests: "error",
  timesheet: "warning",
  payroll: "success",
  evaluations: "muted",
};

/**
 * The only module tags on the Super Admin activity log (column + filter) —
 * the PROLINE-3943 `module` enum.
 */
export const ADMIN_ACTIVITY_MODULES = [
  "companies",
  "clients",
  "events",
  "joiningRequests",
  "timesheet",
  "payroll",
  "evaluations",
  "teamPermissions",
];

/**
 * Activity log API `module` key (PROLINE-3943) → module id above. Also used
 * the other way round to send the Module filter.
 */
export const ACTIVITY_MODULE_BY_API_KEY = {
  companies: "companies",
  clients: "clients",
  events: "events",
  joining_requests: "joiningRequests",
  timesheet: "timesheet",
  payroll: "payroll",
  evaluations: "evaluations",
  team_and_permissions: "teamPermissions",
};

/**
 * Company portal API `module` key (PROLINE-3925) → module id: the same keys,
 * except `companies` is the company's own "Company Account" tag there.
 */
export const COMPANY_ACTIVITY_MODULE_BY_API_KEY = {
  ...ACTIVITY_MODULE_BY_API_KEY,
  companies: "companyAccount",
};

/**
 * GET /activity_logs entry (PROLINE-3943 / 3925) → activity log row (dashboard
 * card + full log table). A deleted actor / company comes back `null` and shows
 * a dash; so does a `null` or unknown `module` (`module` is then undefined).
 * `moduleByApiKey` picks the portal's module mapping.
 */
export const toActivityLogRow = (
  entry,
  moduleByApiKey = ACTIVITY_MODULE_BY_API_KEY,
) => ({
  id: entry.id,
  createdAt: entry.created_at,
  actor: { name: entry.actor?.name || "", role: entry.actor?.role_name || "" },
  companyId: entry.company?.id ?? null,
  company: entry.company?.name ?? null,
  action: entry.action || "",
  module: moduleByApiKey[entry.module],
});

/** The only module tags on the company portal activity log (column + filter). */
export const COMPANY_ACTIVITY_MODULES = [
  "companyAccount",
  "teamPermissions",
  "events",
  "payroll",
  "evaluations",
  "timesheet",
];

/**
 * "28 Jul 2026, 09:14 AM" / "28 يوليو 2026، 09:14 ص" — Latin digits in both
 * languages, 12-hour time. Date and time are formatted apart: some engines
 * (Safari) join them with "at" instead of ",". The English time uses en-US for
 * an upper-case "AM / PM" (en-GB gives "am / pm"). Shown in Riyadh time — the
 * API's `created_at` zone — whatever the viewer's device time zone.
 */
export const formatActivityDateTime = (date, lang) => {
  const isAr = lang === "ar";
  const value = new Date(date);
  const day = value.toLocaleDateString(isAr ? "ar-EG-u-nu-latn" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Riyadh",
  });
  const time = value.toLocaleTimeString(isAr ? "ar-EG-u-nu-latn" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Riyadh",
  });
  return `${day}${isAr ? "، " : ", "}${time}`;
};
